import { useState, useCallback } from 'react';
import api from '../services/api';

function getProviders(execution) {
  const repos = execution?.repos || [];
  const providers = new Set(repos.map(r => r.provider).filter(Boolean));
  if (providers.size === 0) providers.add('github');
  return [...providers];
}


export function useCreatePR({ execution, fallbackTitle = 'Code changes from analysis' }) {
  const [creatingRepos, setCreatingRepos] = useState({});

  const codeImplementation = execution?.artifacts?.codeImplementation;
  const canCreatePR = Boolean(codeImplementation?.diff);
  const creatingPR = Object.values(creatingRepos).some(Boolean);

  const handleCreatePR = useCallback(async (targetRepo) => {
    const key = targetRepo || '__all__';
    if (!execution || !canCreatePR) {
      alert('No code changes to create a PR from');
      return;
    }

    setCreatingRepos(prev => ({ ...prev, [key]: true }));
    try {
      const prTitle = codeImplementation.prTitle || execution.name || fallbackTitle;
      const prDescription = codeImplementation.prDescription || undefined;
      const providers = getProviders(execution);
      const payload = {
        executionId: execution.executionId || execution.id,
        title: prTitle,
        ...(prDescription && { description: prDescription }),
        ...(targetRepo && { targetRepo })
      };

      const allPRs = [];
      const allErrors = [];

      for (const provider of providers) {
        const endpoint = provider === 'gitlab' ? '/gitlab/create-mr' : '/github/create-pr';
        const label = provider === 'gitlab' ? 'MR' : 'PR';

        console.log(`📝 Creating ${label} via ${endpoint}:`, {
          title: prTitle,
          provider,
          hasDescription: !!prDescription
        });

        try {
          const response = await api.post(endpoint, payload);

          if (response.pullRequests?.length) {
            allPRs.push(...response.pullRequests);
          } else if (response.prUrl) {
            allPRs.push({ prUrl: response.prUrl, repo: provider });
          }
        } catch (providerError) {
          const msg = providerError.message || 'Unknown error';
          console.warn(`⚠️ Failed to create ${label} for ${provider}:`, msg);
          allErrors.push(`${provider}: ${msg}`);
        }
      }

      if (allPRs.length === 0) {
        const detail = allErrors.length > 0 ? allErrors.join('\n') : 'No changes detected in any repository.';
        alert(`Failed to create PR/MR:\n\n${detail}`);
        return;
      }

      for (const pr of allPRs) {
        window.open(pr.prUrl, '_blank');
      }
    } catch (error) {
      console.error('Error creating PR/MR:', error);
      alert(`Failed to create PR/MR: ${error.message || 'Unknown error'}`);
    } finally {
      setCreatingRepos(prev => ({ ...prev, [key]: false }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [execution, fallbackTitle, canCreatePR]);

  const isCreatingRepo = useCallback((repoName) => {
    return !!creatingRepos[repoName || '__all__'];
  }, [creatingRepos]);

  return { creatingPR, isCreatingRepo, handleCreatePR, canCreatePR };
}

export default useCreatePR;
